import React, { useEffect, useState } from "react";
import useBase64Image from "../../hooks/useBase64Image";

type RGB = [number, number, number];

const swapColorExact = async (imageSrc: string, from: RGB, to: RGB): Promise<string> => {
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context not available");

  ctx.drawImage(img, 0, 0);
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imgData.data;

  const [fr, fg, fb] = from;
  const [tr, tg, tb] = to;

  for (let i = 0; i < d.length; i += 4) {
    if (d[i] === fr && d[i + 1] === fg && d[i + 2] === fb) {
      d[i] = tr;
      d[i + 1] = tg;
      d[i + 2] = tb;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL("image/png");
};

const Base64Image: React.FC<{
  imageString: string | null;
  alt?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  colorSwap?: { from: RGB; to: RGB };
  testDataId?: string;
}> = ({ imageString, alt, style, onLoad, onError, colorSwap, testDataId }) => {
  const baseImageSrc = useBase64Image(imageString);
  const [finalSrc, setFinalSrc] = useState<string | null>(baseImageSrc);

  useEffect(() => {
    if (!baseImageSrc) {
      return;
    }

    if (!colorSwap) {
      setFinalSrc(baseImageSrc);
      return;
    }

    const tryColorSwapAsync = async () => {
      try {
        const swapped = await swapColorExact(baseImageSrc, colorSwap.from, colorSwap.to);
        setFinalSrc(swapped);
      } catch (err) {
        console.error("Color swap failed:", err);
        setFinalSrc(baseImageSrc);
      }
    };

    tryColorSwapAsync();
  }, [baseImageSrc, colorSwap]);

  if (!finalSrc) {
    return null;
  }

  return <img src={finalSrc} alt={alt} style={style} onLoad={onLoad} onError={onError} data-testid={testDataId} />;
};

export default Base64Image;
